import styles from './client-configuration.module.scss';
import Header from 'components/common/SecondaryHeader/Header';
import { useTranslation } from 'react-i18next';
import { addPlusMinus5PercentageRangeToNumber, addThousandSeparator } from 'utils/numbers';
import { useSelector, useDispatch } from 'react-redux';
import { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { lastElement } from 'utils/arrays';
import { Link, Breadcrumbs, Button, Box, Tab } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { ReactComponent as EyeIcon } from 'icons/eye.svg';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import ClientSharingSettings from './ClientSharingSettings';
import { get, getActivityLogs } from 'api/externalAccess';
import actions from 'store/actions';
import Loading from 'components/common/Loading/Loading';
import ClientActivity from './ClientActivity';
import { getFormatSeparatorsOptions } from 'config/marketConfig';
import searchParams from 'constants/searchParams';
import paths from 'constants/paths';
import { getFullPathOrHome, openFullPathInNewTab } from 'utils/paths';

export default function ClientConfiguration() {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const params = new URLSearchParams(window.location.search);
  const activeTab = params.get(searchParams.activeTab);
  const accessKey = params.get(searchParams.accessKey);
  const na = useSelector((state) => state.na);
  const naSpaceSelected = na.spaces.find((naSpace) => naSpace.id === na.workplacestyleSelected);
  const externalAccessConfig = useSelector((state) => state.externalAccessConfig);
  const totalSeats = lastElement(na.growth.map((growth_row) => growth_row.seats));
  const [tabSelected, setTabSelected] = useState(activeTab ? activeTab : '1');
  const [loading, setLoading] = useState(true);
  const [clientActivity, setClientActivity] = useState([]);

  const getExternalAccess = useCallback(async () => {
    const response = await get(accessKey);
    dispatch(actions.externalAccess.setExternalAccessConfig(response));
    const clientActivityResponse = await getActivityLogs(response.accessKey);
    setClientActivity(clientActivityResponse);
    setLoading(false);
  }, [dispatch, accessKey]);

  useEffect(() => {
    getExternalAccess();
  }, [getExternalAccess]);

  const handleBreadcrumbsClick = () => {
    history.push(getFullPathOrHome(paths.library));
  };

  const openAssessment = () => {
    openFullPathInNewTab(`${paths.step}${paths.steps.yourPeople}/${na.id}`);
  };

  const handleTabChange = (newValue) => {
    setTabSelected(newValue);
  };

  const breadcrumbs = [
    <Link
      data-testid="BreadcrumbsLink"
      underline="hover"
      key="1"
      color="inherit"
      component="button"
      onClick={handleBreadcrumbsClick}>
      {t('library.title')}
    </Link>,
    <span key="2">{na.name}</span>,
  ];

  return (
    <div className={styles.cc__container}>
      <Header />

      <div className={styles.cc__main}>
        <Breadcrumbs
          data-testid="ClientConfiguration-Breadcrumbs"
          classes={{ separator: styles.custom_breadcrumbs__separator }}
          className={styles.custom_breadcrumbs}
          separator={<NavigateNextIcon fontSize="small" />}>
          {breadcrumbs}
        </Breadcrumbs>
        <div className={styles.cc__assessment_title_container}>
          <h2 className={styles.cc__assessment_title}>{na.name}</h2>
          <Button
            data-testid="AssessmentButton"
            className={styles.cc_assessment_link}
            size="small"
            startIcon={<EyeIcon />}
            onClick={openAssessment}>
            {t('clientConfig.assessmentButton')}
          </Button>
        </div>
        <div className={styles.cc__content_container}>
          <div className={styles.cc__info_container}>
            <div className={`${styles.cc__office_info_card} ${styles.cc__office_info_card_rsf}`}>
              <span className={styles.cc__office_info_value}>
                {addPlusMinus5PercentageRangeToNumber(
                  naSpaceSelected.rentableSquareFeet,
                  getFormatSeparatorsOptions(),
                )}
              </span>
              <h3 className={styles.cc__office_info_title}>{t('step6.rentableTotal')}</h3>
            </div>
            <div
              className={`${styles.cc__office_info_card} ${styles.cc__office_info_card_per_employee}`}>
              <span className={styles.cc__office_info_value}>
                {addThousandSeparator(
                  naSpaceSelected.rentableSquareFeetPerHead,
                  getFormatSeparatorsOptions(),
                )}
              </span>
              <h3 className={styles.cc__office_info_title}>{t('step6.inOfficeAndHybridTotal')}</h3>
            </div>
            <div className={`${styles.cc__office_info_card} ${styles.cc__office_info_card_seats}`}>
              <span className={styles.cc__office_info_value}>
                {addThousandSeparator(totalSeats, getFormatSeparatorsOptions())}
              </span>
              <h3 className={styles.cc__office_info_title}>
                {t('step6.inOfficeAndHybridEmployees')}
              </h3>
            </div>
          </div>
          <div className={styles.cc__tabs_container}>
            <TabContext value={tabSelected}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList
                  sx={{ minHeight: 'unset' }}
                  className={styles.cc__tabs}
                  onChange={(_, value) => handleTabChange(value)}>
                  <Tab
                    data-testid="ConfiogurationTab1"
                    className={styles.cc__tab}
                    label={t('clientConfig.sharingWithClientTab')}
                    value="1"
                  />
                  <Tab
                    data-testid="ConfiogurationTab2"
                    className={styles.cc__tab}
                    label={t('clientConfig.clientActivityTab')}
                    value="2"
                  />
                </TabList>
              </Box>
              <TabPanel className={styles.cc__tab_panel} value="1">
                {loading ? <Loading /> : <ClientSharingSettings />}
              </TabPanel>
              <TabPanel className={styles.cc__tab_panel} value="2">
                {loading ? (
                  <Loading />
                ) : (
                  <ClientActivity
                    clientActivityList={clientActivity}
                    brokerNaId={externalAccessConfig.brokerNaId}
                    clientNaId={externalAccessConfig.clientNaId}
                  />
                )}
              </TabPanel>
            </TabContext>
          </div>
        </div>
      </div>
    </div>
  );
}
